import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPcsData } from './pcsData';
import { formatDateLocale } from '../utils/formatters';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

const PM10PcsChart = ({ readings, darkMode = false }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  
  // Détection portée par l'ENSEMBLE des readings (cf. pcsData) : une session dont la
  // 1ʳᵉ reading n'a pas de pcs mais qui en a ailleurs doit quand même afficher le graphique.
  if (!hasPcsData(readings)) {
    return null;
  }

  const labels = readings.map(r =>
    // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
    formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' })
  );

  // Calculer le max pour l'échelle Y AVANT de créer options
  const maxPM10Pcs = Math.max(...readings.map(r => r.pm10_pcs || 0));
  const yAxisMax = Math.ceil(maxPM10Pcs * 1.2); // 20% marge + arrondi supérieur

  const data = {
    labels,
    datasets: [
      {
        label: t('charts.pm10pcs.series'),
        data: readings.map(r => r.pm10_pcs),
        borderColor: darkMode ? 'rgba(168, 85, 247, 0.9)' : 'rgba(147, 51, 234, 1)',
        backgroundColor: darkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(147, 51, 234, 0.15)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
        tension: 0.3
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: darkMode ? '#E1E1E1' : '#2C3E50',
          font: { size: 12, weight: '600' },
          usePointStyle: true,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: darkMode ? '#E1E1E1' : '#2C3E50',
        bodyColor: darkMode ? '#E8EDF2' : '#4A5568',
        borderColor: darkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(147, 51, 234, 0.5)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(0)} pcs/L`;
          }
        }
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'shift'
        },
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.1
          },
          pinch: {
            enabled: true
          },
          mode: 'x'
        },
        limits: {
          x: { min: 'original', max: 'original' },
          y: { min: 'original', max: 'original' }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          drawBorder: false
        },
        ticks: {
          color: darkMode ? '#E8EDF2' : '#4A5568',
          font: { size: 11 },
          maxTicksLimit: 20
        }
      },
      y: {
        beginAtZero: true,
        max: yAxisMax, // Utilise la valeur pré-calculée
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          drawBorder: false
        },
        ticks: {
          color: darkMode ? '#E8EDF2' : '#4A5568',
          font: { size: 11 }
        },
        title: {
          display: true,
          text: t('charts.pm10pcs.axis'),
          color: darkMode ? '#E8EDF2' : '#4A5568',
          font: { size: 12, weight: '600' }
        }
      }
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <h3 style={{
        fontSize: '16px',
        fontWeight: '700',
        color: darkMode ? '#E1E1E1' : '#2C3E50',
        marginBottom: '12px'
      }}>
        {t('charts.pm10pcs.title')}
      </h3>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button
          onClick={() => { if (chartRef.current) chartRef.current.resetZoom(); }}
          style={{
            padding: '6px 12px',
            background: '#1B77F1',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {t('common.zoom.reset')}
        </button>
      </div>
      <div style={{ height: '250px' }}>
        <Line ref={chartRef} data={data} options={options} />
      </div>
        
      {/* Instructions zoom */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(27, 119, 241, 0.05)',
        borderRadius: '6px',
        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(27, 119, 241, 0.15)',
        fontSize: '13px',
        color: darkMode ? '#E8EDF2' : '#4A5568',
        textAlign: 'center',
        lineHeight: '1.5'
      }}>
        <span style={{ display: 'none' }} className="mobile-only">
          💡 {t('common.zoom.touch')}
        </span>
        <span className="desktop-only">
          💡 {t('common.zoom.desktop')}
        </span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: darkMode ? '#B8BFC7' : '#6B7280', margin: '8px 0 0', fontStyle: 'italic', lineHeight: '1.5' }}>
        {t('charts.pm10pcs.footnote')}
      </p>
    </div>
  );
};

export default PM10PcsChart;
